import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { workflowApi, WorkflowStep } from '../services/api';
import { PageHeader, EmptyState, LoadingState, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { fehlerText } from '../vertrieb/format';
import { schritteAus, variablenAus } from '../inhalte/workflowSchritte';

const EditWorkflowPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { push } = useToast();
  const { confirm } = useConfirm();
  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [schritte, setSchritte] = useState<WorkflowStep[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [gefunden, setGefunden] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.resolve()
      .then(() => workflowApi.getWorkflow(id))
      .then((wf) => {
        if (!wf) { setGefunden(false); return; }
        setName(wf.name || '');
        setBeschreibung(wf.description || '');
        setSchritte(schritteAus(wf));
      })
      .catch((e) => { setFehler(fehlerText(e, 'Workflow konnte nicht geladen werden')); setGefunden(false); })
      .finally(() => setLaedt(false));
  }, [id]);

  const aendern = (i: number, feld: keyof WorkflowStep, wert: any) =>
    setSchritte((alt) => alt.map((s, j) => (j === i ? { ...s, [feld]: wert } as WorkflowStep : s)));

  const hinzufuegen = () => setSchritte((alt) => [...alt, { id: `step-${Date.now()}`, name: `Schritt ${alt.length + 1}`, provider: 'openai', prompt: '' }]);

  const verschieben = (i: number, um: number) => setSchritte((alt) => {
    const ziel = i + um;
    if (ziel < 0 || ziel >= alt.length) return alt;
    const next = [...alt];
    [next[i], next[ziel]] = [next[ziel], next[i]];
    return next;
  });

  const entfernen = async (i: number) => {
    const s = schritte[i];
    if (s.prompt.trim()) {
      const ok = await confirm({ title: 'Schritt entfernen?', message: `„${s.name}“ und sein Prompt werden entfernt, sobald du speicherst.`, confirmLabel: 'Entfernen', tone: 'danger' });
      if (!ok) return;
    }
    setSchritte((alt) => alt.filter((_, j) => j !== i));
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSpeichert(true);
    setFehler(null);
    try {
      await workflowApi.updateWorkflow(id, { name, description: beschreibung, steps: schritte });
      push({ message: `„${name}“ gespeichert.`, severity: 'success' });
      navigate('/workflows');
    } catch (err) {
      setFehler(fehlerText(err, 'Speichern fehlgeschlagen'));
    } finally {
      setSpeichert(false);
    }
  };

  if (laedt) return <div className="page-container"><LoadingState rows={4} label="Workflow wird geladen" /></div>;
  if (!gefunden) {
    return <div className="page-container"><EmptyState icon="workflow" title="Workflow nicht gefunden" description={fehler ?? undefined} action={{ label: 'Meine Workflows', to: '/workflows' }} /></div>;
  }

  const eingaben = variablenAus(schritte.map((s) => s.prompt));

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={<Link to="/workflows"><Icon name="arrow-left" size={12} /> Meine Workflows</Link>}
        title={`Workflow bearbeiten`}
        subtitle="Jeder Schritt hat eigenen Prompt und Anbieter. Mit {{step_<id>_result}} greift ein Schritt auf das Ergebnis eines früheren zu."
      />
      <form onSubmit={speichern} aria-label="Workflow bearbeiten">
        <section className="card inhalt-formular">
          <div className="inhalt-felder">
            <label>Name *<input required value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="inhalt-felder__breit">Beschreibung<textarea rows={2} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} /></label>
          </div>
          {eingaben.length > 0 && (
            <p className="text-xs text-secondary">Beim Ausführen abgefragt: {eingaben.map((v) => <code key={v}>{`{{${v}}}`} </code>)}</p>
          )}
        </section>

        <div className="inhalt-ergebnis__kopf">
          <h2 className="flow-section-title">Schritte ({schritte.length})</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={hinzufuegen}><Icon name="plus" size={14} /> Schritt</button>
        </div>

        {schritte.length === 0 && <EmptyState compact icon="workflow" title="Noch kein Schritt" action={{ label: 'Ersten Schritt anlegen', onClick: hinzufuegen }} />}

        {schritte.map((s, i) => (
          <section key={s.id} className="card workflow-schritt" aria-label={`Schritt ${i + 1}`}>
            <div className="inhalt-ergebnis__kopf">
              <h3 className="card-title"><span className="step-number">{i + 1}</span> {s.name || 'Ohne Namen'}</h3>
              <div className="btn-group">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => verschieben(i, -1)} disabled={i === 0} aria-label={`Schritt ${i + 1} nach oben`}>↑</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => verschieben(i, 1)} disabled={i === schritte.length - 1} aria-label={`Schritt ${i + 1} nach unten`}>↓</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => entfernen(i)} aria-label={`Schritt ${i + 1} entfernen`}><Icon name="trash" size={14} /></button>
              </div>
            </div>
            <div className="inhalt-felder">
              <label>Name *<input required value={s.name} onChange={(e) => aendern(i, 'name', e.target.value)} /></label>
              <label>
                Anbieter
                <select value={s.provider} onChange={(e) => aendern(i, 'provider', e.target.value)}>
                  <option value="openai">OpenAI</option>
                  <option value="claude">Claude</option>
                  <option value="perplexity">Perplexity</option>
                </select>
              </label>
              <label className="inhalt-felder__breit">Prompt *<textarea required rows={5} value={s.prompt} onChange={(e) => aendern(i, 'prompt', e.target.value)} /></label>
            </div>
            <details className="inhalt-details" open={Boolean(s.systemPrompt)}>
              <summary>System-Prompt (optional)</summary>
              <textarea className="form-textarea" rows={2} aria-label={`System-Prompt Schritt ${i + 1}`} value={s.systemPrompt || ''} onChange={(e) => aendern(i, 'systemPrompt', e.target.value)} />
            </details>
            <p className="text-xs text-secondary">Ergebnis für spätere Schritte: <code>{`{{step_${s.id}_result}}`}</code></p>
          </section>
        ))}

        {fehler && <div className="alert alert-error">{fehler}</div>}

        <div className="btn-group">
          <button type="submit" className="btn btn-primary" disabled={speichert || schritte.length === 0}>{speichert ? 'Speichert…' : 'Speichern'}</button>
          <Link to="/workflows" className="btn btn-ghost">Abbrechen</Link>
        </div>
      </form>
    </div>
  );
};

export default EditWorkflowPage;
